import {useIssueState} from '@github-ui/issue-pr-state'
import type {IssueStateReasonType, IssueStateType} from '@github-ui/issue-pr-state/constants'
import {issueHovercardPath, issueLinkedPullRequestHovercardPath} from '@github-ui/paths'
import type {SafeHTMLString} from '@github-ui/safe-html'
import {VerifiedHTMLBox} from '@github-ui/safe-html/VerifiedHTML'
import {LockIcon} from '@primer/octicons-react'
import {Link} from '@primer/react'
import {Tooltip} from '@primer/react/deprecated'
import {Octicon} from '@primer/styled-react/deprecated'
import {clsx} from 'clsx'
import {graphql, useFragment} from 'react-relay'

import {LABELS} from '../constants/labels'
import {formatIssueReference} from '../utils/format-issue-reference'
import type {IssueLink$key} from './__generated__/IssueLink.graphql'
import styles from './IssueLink.module.css'

type IssueLinkProps = {
  data: IssueLink$key
  targetOwnerLogin: string
  targetRepositoryId: string
  inline?: boolean
}

export function IssueLink({
  data,
  targetRepositoryId,
  targetOwnerLogin,
  inline = false,
}: IssueLinkProps): React.ReactElement {
  const source = useFragment(
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    graphql`
      fragment IssueLink on ReferencedSubject {
        __typename
        ... on Issue {
          id
          issueTitleHTML: titleHTML
          url
          number
          stateReason
          repository {
            id
            name
            isPrivate
            owner {
              login
            }
          }
        }
        ... on PullRequest {
          id
          pullTitleHTML: titleHTML
          url
          number
          state
          isDraft
          isInMergeQueue
          repository {
            id
            name
            isPrivate
            owner {
              login
            }
          }
        }
      }
    `,
    data,
  )
  const isIssue = source.__typename === 'Issue'
  const isPullRequest = source.__typename === 'PullRequest'

  const state = (isPullRequest ? source.state : undefined) as IssueStateType
  const stateReason = (isIssue ? source.stateReason : undefined) as IssueStateReasonType
  const {sourceIcon} = useIssueState({state, stateReason})
  const isDraft = isPullRequest ? source.isDraft : undefined
  const isInMergeQueue = isPullRequest ? source.isInMergeQueue : undefined
  const icon = sourceIcon(source.__typename as 'Issue' | 'PullRequest', isDraft, isInMergeQueue)

  if (!isIssue && !isPullRequest) {
    return <></>
  }
  const dataHovercardUrl =
    source.repository && source.number
      ? isIssue
        ? issueHovercardPath({
            repo: source.repository.name,
            owner: source.repository.owner.login,
            issueNumber: source.number,
          })
        : issueLinkedPullRequestHovercardPath({
            owner: source.repository.owner.login,
            repo: source.repository.name,
            pullRequestNumber: source.number,
          })
      : null

  const issueReference = formatIssueReference(
    {
      number: source.number,
      ownerLogin: source.repository.owner.login,
      repoId: source.repository.id,
      repoName: source.repository.name,
    },
    targetRepositoryId,
    targetOwnerLogin,
  )

  const privateDescriptionId = `${source.id}-private-description`

  // Defining the title in this way avoids a problem that stems from the titleHTML field being defined as different
  // types in the Issue and PullRequest platform objects.
  const titleHTML = isIssue ? source.issueTitleHTML : source.pullTitleHTML

  const InlineLink = (
    <>
      <Octicon icon={icon} size={16} sx={{mr: inline ? '4px' : 'initial', mt: inline ? 'initial' : '2px'}} />
      <Link
        href={source.url}
        data-hovercard-url={dataHovercardUrl}
        aria-describedby={source.repository.isPrivate ? privateDescriptionId : undefined}
        inline
        className={styles.issueLinkAnchor}
      >
        <VerifiedHTMLBox
          as="bdi"
          className={clsx('markdown-title', styles.issueTitleContainer)}
          /* eslint-disable-next-line @github-ui/github-monorepo/no-cast-as-safe-html-string */
          html={titleHTML as SafeHTMLString}
        />
        <span className={styles.repoIssueNumber}> {issueReference}</span>
      </Link>
      {source.repository.isPrivate && source.repository.id !== targetRepositoryId && (
        <>
          <Tooltip
            aria-label={LABELS.crossReferencedEventLockTooltip(
              `${source.repository.owner.login}/${source.repository.name}`,
            )}
          >
            <Octicon icon={LockIcon} />
          </Tooltip>
          <span id={privateDescriptionId} className="sr-only">
            {LABELS.crossReferencedEvent.privateEventDescription}
          </span>
        </>
      )}
    </>
  )

  return inline ? <>{InlineLink}</> : <div className={styles.issueContainer}>{InlineLink}</div>
}
